import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Wallet, CheckCircle2, X, Users2, ClipboardCheck, BarChart3, ClipboardList, Download } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '../lib/api';
import { PageHeader, Button, Card, Input, Select, Modal, Badge, Th, Td, EmptyState } from '../components/ui';
import { fmtMoney, fmtNum, fmtDate, canWrite, canDelete, SIZES, groupLogsByDate } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import TailorSummaryPrint from '../components/TailorSummaryPrint';
import { downloadElementAsPdf } from '../lib/pdf';

export default function Tailors() {
  const { user } = useAuth();
  const write = canWrite(user.role);
  const [tab, setTab] = useState('roster');
  const [tailors, setTailors] = useState([]);
  const [logs, setLogs] = useState([]);
  const [payments, setPayments] = useState([]);
  const [edit, setEdit] = useState(null);
  const [pay, setPay] = useState(null);
  const [dash, setDash] = useState([]);
  const [products, setProducts] = useState([]);
  const [logOpen, setLogOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const [editLog, setEditLog] = useState(null);

  const load = () => {
    api.get('/tailors').then((r) => setTailors(r.data));
    api.get('/daily-logs').then((r) => setLogs(r.data));
    api.get('/tailor-payments').then((r) => setPayments(r.data));
    api.get('/dashboard/tailors').then((r) => setDash(r.data)).catch(() => {});
    api.get('/products').then((r) => setProducts(r.data));
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => { if (!window.confirm('Delete tailor?')) return; await api.delete(`/tailors/${id}`); toast.success('Deleted'); load(); };
  const approve = async (id) => { try { await api.post(`/daily-logs/${id}/approve`); toast.success('Approved · stock & wages updated'); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const delLog = async (id) => {
    if (!window.confirm('Delete this production log? Stock and wages will be reversed if it was approved.')) return;
    try { await api.delete(`/daily-logs/${id}`); toast.success('Log deleted'); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const discard = async (id) => { await api.delete(`/daily-logs/${id}`); toast.success('Discarded'); load(); };

  const pending = logs.filter((l) => !l.approved);

  return (
    <div>
      <PageHeader title="Tailors" subtitle="Roster, piece-rate wages & timesheet approvals"
        actions={write && <div className="flex gap-2">
          <Button variant="outline" onClick={() => setLogOpen(true)} data-testid="admin-log-output-btn"><ClipboardList size={16} /> Log Output</Button>
          <Button onClick={() => setEdit('new')} data-testid="add-tailor-btn"><Plus size={16} /> Tailor</Button>
        </div>} />

      <div className="flex gap-1 mb-4 bg-slate-100 p-1 rounded-md w-fit">
        {[['roster', 'Roster', Users2], ['approvals', `Approvals${pending.length ? ` (${pending.length})` : ''}`, ClipboardCheck], ['dashboard', 'Dashboard', BarChart3]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tailor-tab-${k}`}
            className={`flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded transition-colors ${tab === k ? 'bg-white text-[#1E3A8A] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {tab === 'dashboard' && (
        <Card className="overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 text-sm font-heading font-bold text-slate-700">Tailor-wise Production (Till Date) <span className="font-normal text-slate-400">· click a row for date-wise summary</span></div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr><Th>Tailor</Th><Th right>Pieces</Th><Th right>Dozens</Th><Th right>Damage</Th><Th right>Earned</Th><Th right>Paid</Th><Th right>Pending</Th><Th>Last Activity</Th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {dash.map((d) => (
                  <tr key={d.tailorId} data-testid="tailor-dash-row" onClick={() => setSummary(d)} className="hover:bg-slate-50/60 cursor-pointer">
                    <Td className="font-medium text-slate-800">{d.name}</Td>
                    <Td right className="num font-semibold">{fmtNum(d.pieces)}</Td>
                    <Td right className="num">{fmtNum(d.dozens)}</Td>
                    <Td right className={`num ${d.damage > 0 ? 'text-rose-600' : 'text-slate-400'}`}>{fmtNum(d.damage)}</Td>
                    <Td right className="num">{fmtMoney(d.earned)}</Td>
                    <Td right className="num text-emerald-600">{fmtMoney(d.paid)}</Td>
                    <Td right className={`num font-semibold ${d.pending > 0 ? 'text-[#B45309]' : 'text-emerald-600'}`}>{fmtMoney(d.pending)}</Td>
                    <Td>{d.lastDate ? fmtDate(d.lastDate) : '—'}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!dash.length && <EmptyState title="No production yet" icon={BarChart3} />}
        </Card>
      )}
      {tab === 'roster' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tailors.map((t) => (
            <Card key={t.id} className="p-4" data-testid="tailor-card">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-heading font-bold text-slate-900">{t.name}</p>
                  <p className="text-xs text-slate-400">{t.phone}</p>
                </div>
                <Badge className="bg-slate-100 border-slate-200 text-slate-600">{fmtMoney(t.paymentRate)}/dz</Badge>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                <div className="bg-slate-50 rounded p-2">
                  <p className="text-[10px] text-slate-400 uppercase">Earned</p>
                  <p className="text-sm font-semibold num text-slate-800">{fmtNum(t.financials?.totalEarned)}</p>
                </div>
                <div className="bg-slate-50 rounded p-2">
                  <p className="text-[10px] text-slate-400 uppercase">Paid</p>
                  <p className="text-sm font-semibold num text-emerald-600">{fmtNum(t.financials?.totalPaid)}</p>
                </div>
                <div className="bg-amber-50 rounded p-2">
                  <p className="text-[10px] text-amber-500 uppercase">Pending</p>
                  <p className="text-sm font-semibold num text-[#B45309]">{fmtNum(t.financials?.pendingPayment)}</p>
                </div>
              </div>
              {write && (
                <div className="flex gap-2 mt-4">
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => setPay(t)} data-testid="pay-tailor-btn"><Wallet size={14} /> Pay</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEdit(t)}><Pencil size={14} /></Button>
                  {canDelete(user.role) && <Button size="sm" variant="ghost" onClick={() => del(t.id)}><Trash2 size={14} /></Button>}
                </div>
              )}
            </Card>
          ))}
          {!tailors.length && <EmptyState title="No tailors" icon={Users2} />}
        </div>
      )}
      {tab === 'approvals' && (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr><Th>Date</Th><Th>Tailor</Th><Th>Product</Th><Th>Size</Th><Th right>Dozens</Th><Th right>Earnings</Th><Th>Status</Th><Th className="text-right">Action</Th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {logs.map((l) => (
                  <tr key={l.id} className="hover:bg-slate-50/60 transition-colors" data-testid="log-row">
                    <Td>{fmtDate(l.date)}</Td>
                    <Td className="font-medium text-slate-800">{l.tailorName}</Td>
                    <Td>{l.productName}</Td>
                    <Td>{l.size || 'Universal'}</Td>
                    <Td right>{fmtNum(l.quantityDozens)}</Td>
                    <Td right>{fmtMoney(l.totalEarnings)}</Td>
                    <Td>{l.approved ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Approved</Badge> : <Badge className="bg-amber-50 text-amber-700 border-amber-200">Pending</Badge>}</Td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      {!l.approved && write && <>
                        <Button size="sm" variant="success" onClick={() => approve(l.id)} data-testid="approve-log-btn"><CheckCircle2 size={13} /> Approve</Button>
                        <Button size="sm" variant="ghost" className="ml-1" onClick={() => discard(l.id)}><X size={13} /></Button>
                      </>}
                      {canDelete(user.role) && <>
                        <Button size="sm" variant="ghost" className="ml-1" onClick={() => setEditLog(l)} data-testid="edit-log-btn"><Pencil size={13} /></Button>
                        <Button size="sm" variant="ghost" className="ml-1" onClick={() => delLog(l.id)} data-testid="delete-log-btn"><Trash2 size={13} /></Button>
                      </>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!logs.length && <EmptyState title="No timesheets submitted" icon={ClipboardCheck} />}
        </Card>
      )}

      {edit && <TailorModal tailor={edit === 'new' ? null : edit} products={products} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {pay && <PayModal tailor={pay} onClose={() => setPay(null)} onSaved={() => { setPay(null); load(); }} />}
      {logOpen && <AdminLogModal tailors={tailors} products={products} onClose={() => setLogOpen(false)} onSaved={() => { setLogOpen(false); load(); }} />}
      {summary && <DateSummaryModal tailor={summary} logs={logs.filter((l) => l.tailorId === summary.tailorId)} onClose={() => setSummary(null)} />}
      {editLog && <EditLogModal log={editLog} products={products} onClose={() => setEditLog(null)} onSaved={() => { setEditLog(null); load(); }} />}
    </div>
  );
}

function EditLogModal({ log, products, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(log.date);
  const [lines, setLines] = useState(
    log.items?.length
      ? log.items.map((i) => ({ productId: i.productId, dozens: i.dozens, size: i.size || 'Universal' }))
      : [{ productId: log.productId, dozens: log.quantityDozens, size: log.size || 'Universal' }]
  );
  const setLine = (i, patch) => setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const totalDz = lines.reduce((s, l) => s + (Number(l.dozens) || 0), 0);
  const save = async () => {
    const items = lines.filter((l) => l.productId && Number(l.dozens) > 0)
      .map((l) => ({ productId: l.productId, dozens: Number(l.dozens), size: l.size }));
    if (!items.length) { toast.error('Add at least one product with dozens'); return; }
    try {
      await api.put(`/daily-logs/${log.id}`, { date, items });
      toast.success('Log updated'); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal open title={`Edit Log · ${log.tailorName}`} onClose={onClose} size="sm"
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-edit-log-btn">Save</Button></>}>
      <div className="space-y-4">
        <Input label="Date" type="date" value={date} max={today} onChange={(e) => setDate(e.target.value)} data-testid="edit-log-date" />
        <LogLines lines={lines} setLines={setLines} setLine={setLine} products={products} totalDz={totalDz} idPrefix="edit-log" />
        {log.approved && <p className="text-xs text-amber-600">This log is approved — stock will be adjusted automatically to match the new values.</p>}
      </div>
    </Modal>
  );
}

export function LogLines({ lines, setLines, setLine, products, totalDz, idPrefix }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-slate-600 uppercase">Products stitched</span>
        <Button size="sm" variant="outline" onClick={() => setLines((p) => [...p, { productId: products[0]?.id || '', dozens: '', size: 'Universal' }])} data-testid={`${idPrefix}-add-product-btn`}><Plus size={13} /> Product</Button>
      </div>
      {lines.map((l, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-end" data-testid={`${idPrefix}-product-line`}>
          <div className="col-span-5">
            <Select value={l.productId} onChange={(e) => setLine(i, { productId: e.target.value })} data-testid={`${idPrefix}-product`}>
              {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </div>
          <div className="col-span-3">
            <Input type="number" step="0.5" placeholder="Dozens" value={l.dozens} onChange={(e) => setLine(i, { dozens: e.target.value })} data-testid={`${idPrefix}-dozens`} />
          </div>
          <div className="col-span-3">
            <Select value={l.size} onChange={(e) => setLine(i, { size: e.target.value })} data-testid={`${idPrefix}-size`}>
              {SIZES.map((s) => <option key={s}>{s}</option>)}
            </Select>
          </div>
          <div className="col-span-1 pb-2 text-right">
            {lines.length > 1 && <button onClick={() => setLines((p) => p.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-rose-600"><Trash2 size={14} /></button>}
          </div>
        </div>
      ))}
      <p className="text-[11px] text-slate-400">Total dozens: <span className="num font-semibold">{totalDz}</span> · each product is paid at that product's rate for this tailor</p>
    </div>
  );
}

function DateSummaryModal({ tailor, logs, onClose }) {
  const [month, setMonth] = useState('');
  const [company, setCompany] = useState(null);
  const [downloading, setDownloading] = useState(false);
  useEffect(() => { api.get('/settings').then((r) => setCompany(r.data?.company)).catch(() => {}); }, []);

  const filtered = month ? logs.filter((l) => (l.date || '').startsWith(month)) : logs;
  const rows = groupLogsByDate(filtered);
  const totals = rows.reduce((a, r) => ({ dozens: a.dozens + r.dozens, pieces: a.pieces + r.pieces, earnings: a.earnings + r.earnings }), { dozens: 0, pieces: 0, earnings: 0 });
  const periodLabel = month ? new Date(`${month}-01`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) : 'All Time';

  const download = async () => {
    setDownloading(true);
    try {
      const name = tailor.name.replace(/[^\w\u0600-\u06FF\s-]/g, '').trim().replace(/\s+/g, '-');
      await downloadElementAsPdf(document.getElementById('tailor-summary-print'), `${name}-Production-${month || 'All'}.pdf`);
    } catch { toast.error('Could not generate PDF'); }
    setDownloading(false);
  };

  return (
    <Modal open title={`${tailor.name} · Date-wise Production`} onClose={onClose} size="lg"
      footer={<>
        <Button variant="outline" onClick={onClose}>Close</Button>
        <Button onClick={download} disabled={downloading} data-testid="download-summary-pdf-btn"><Download size={16} /> {downloading ? 'Generating…' : 'Download PDF'}</Button>
      </>}>
      <div className="flex items-end gap-3 mb-4">
        <Input label="Month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="summary-month-input" />
        {month && <Button size="sm" variant="outline" onClick={() => setMonth('')} data-testid="summary-alltime-btn">All Time</Button>}
        <span className="text-xs text-slate-400 pb-2.5">{periodLabel}</span>
      </div>
      <div className="overflow-x-auto max-h-[55vh] overflow-y-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
            <tr><Th>Date</Th><Th>Products</Th><Th right>Dozens</Th><Th right>Pieces</Th><Th right>Earnings</Th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.date} data-testid="date-summary-row" className="hover:bg-slate-50/60">
                <Td className="font-medium text-slate-800">{fmtDate(r.date)}</Td>
                <Td className="text-xs text-slate-500">{Object.entries(r.products).map(([n, q]) => `${n} (${q} dz)`).join(', ')}</Td>
                <Td right className="num font-semibold">{fmtNum(r.dozens)}</Td>
                <Td right className="num">{fmtNum(r.pieces)}</Td>
                <Td right className="num">{fmtMoney(r.earnings)}</Td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="bg-slate-50 border-t border-slate-200">
              <tr>
                <Td className="font-bold text-slate-800">Total</Td><Td />
                <Td right className="num font-bold">{fmtNum(totals.dozens)}</Td>
                <Td right className="num font-bold">{fmtNum(totals.pieces)}</Td>
                <Td right className="num font-bold">{fmtMoney(totals.earnings)}</Td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {!rows.length && <EmptyState title="No production logged" icon={BarChart3} />}
      <div style={{ position: 'fixed', left: '-20000px', top: 0 }}>
        <div id="tailor-summary-print">
          <TailorSummaryPrint tailorName={tailor.name} rows={rows} totals={totals} periodLabel={periodLabel} company={company} />
        </div>
      </div>
    </Modal>
  );
}

function AdminLogModal({ tailors, products, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ tailorId: tailors[0]?.id || '', date: today });
  const [lines, setLines] = useState([{ productId: products[0]?.id || '', dozens: '', size: 'Universal' }]);
  const [autoApprove, setAutoApprove] = useState(true);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setLine = (i, patch) => setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const totalDz = lines.reduce((s, l) => s + (Number(l.dozens) || 0), 0);
  const save = async () => {
    if (!f.tailorId) { toast.error('Select a tailor'); return; }
    const items = lines.filter((l) => l.productId && Number(l.dozens) > 0)
      .map((l) => ({ productId: l.productId, dozens: Number(l.dozens), size: l.size }));
    if (!items.length) { toast.error('Add at least one product with dozens'); return; }
    try {
      const r = await api.post('/daily-logs', { tailorId: f.tailorId, date: f.date, items });
      if (autoApprove) {
        await api.post(`/daily-logs/${r.data.id}/approve`);
        toast.success('Logged & approved · stock & wages updated');
      } else {
        toast.success('Logged · pending approval');
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal open title="Log Tailor Output" onClose={onClose} size="sm"
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="admin-save-log-btn">Save</Button></>}>
      <div className="space-y-4">
        <Select label="Tailor" value={f.tailorId} onChange={(e) => set('tailorId', e.target.value)} data-testid="admin-log-tailor">
          {tailors.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </Select>
        <Input label="Date" type="date" value={f.date} max={today} onChange={(e) => set('date', e.target.value)} data-testid="admin-log-date" />
        <LogLines lines={lines} setLines={setLines} setLine={setLine} products={products} totalDz={totalDz} idPrefix="admin-log" />
        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
          <input type="checkbox" checked={autoApprove} onChange={(e) => setAutoApprove(e.target.checked)} data-testid="admin-log-autoapprove" />
          Approve immediately (updates stock & wages)
        </label>
      </div>
    </Modal>
  );
}

function TailorModal({ tailor, products, onClose, onSaved }) {
  const [f, setF] = useState(tailor || { name: '', phone: '', paymentRate: 10 });
  const [rates, setRates] = useState(tailor?.productRates || {});
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setRate = (pid, v) => setRates((p) => {
    const n = { ...p };
    if (v === '' || v === null) delete n[pid]; else n[pid] = v;
    return n;
  });
  const save = async () => {
    const productRates = {};
    for (const [pid, v] of Object.entries(rates)) {
      const num = parseFloat(v);
      if (!isNaN(num) && num > 0) productRates[pid] = num;
    }
    const payload = { ...f, productRates };
    try {
      if (tailor) await api.put(`/tailors/${tailor.id}`, payload);
      else await api.post('/tailors', payload);
      toast.success('Saved'); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal open title={tailor ? 'Edit Tailor' : 'New Tailor'} onClose={onClose} size="sm"
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-tailor-btn">Save</Button></>}>
      <div className="space-y-4">
        <Input label="Name" value={f.name} onChange={(e) => set('name', e.target.value)} data-testid="tailor-name" />
        <Input label="Phone" value={f.phone} onChange={(e) => set('phone', e.target.value)} />
        <Input label="Default Rate per Dozen (KWD)" type="number" step="0.5" value={f.paymentRate} onChange={(e) => set('paymentRate', parseFloat(e.target.value) || 0)} data-testid="tailor-rate" />
        {products?.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Product-wise Rate per Dozen <span className="normal-case font-normal">(blank = default rate)</span></p>
            <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
              {products.map((p) => (
                <div key={p.id} className="flex items-center gap-3">
                  <span className="text-sm text-slate-700 flex-1 truncate">{p.name}</span>
                  <input type="number" step="0.25" placeholder={`${f.paymentRate}`} value={rates[p.id] ?? ''}
                    onChange={(e) => setRate(p.id, e.target.value)} data-testid={`tailor-product-rate-${p.id}`}
                    className="w-28 border border-slate-200 rounded-md px-2 py-1.5 text-sm num text-right focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30" />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

function PayModal({ tailor, onClose, onSaved }) {
  const [f, setF] = useState({ amount: tailor.financials?.pendingPayment || 0, paymentMethod: 'Cash', reference: '', notes: '' });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      await api.post('/tailor-payments', { tailorId: tailor.id, ...f, amount: Number(f.amount) });
      toast.success('Payment recorded'); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal open title={`Pay ${tailor.name}`} onClose={onClose} size="sm"
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-payment-btn">Record Payment</Button></>}>
      <p className="text-sm text-slate-500 mb-4">Pending balance: <span className="font-semibold num text-[#B45309]">{fmtMoney(tailor.financials?.pendingPayment)}</span></p>
      <div className="space-y-4">
        <Input label="Amount (KWD)" type="number" step="0.001" value={f.amount} onChange={(e) => set('amount', e.target.value)} data-testid="payment-amount" />
        <Select label="Method" value={f.paymentMethod} onChange={(e) => set('paymentMethod', e.target.value)}>
          <option>Cash</option><option>Bank Transfer</option><option>Cheque</option>
        </Select>
        <Input label="Reference" value={f.reference} onChange={(e) => set('reference', e.target.value)} />
      </div>
    </Modal>
  );
}
